// components/CarrinhoPedido.tsx

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  TextField
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { ControladorQuantidade } from '@components/produto/ControladorQuantidade';
import { useNavigate } from 'react-router-dom';
import { SessionUtil, FormatUtil } from '@utils';
import { useCarrinhoContext } from '@contexts';
import { PageHeader } from '@components';

const CarrinhoPedido: React.FC = () => {
  const navigate = useNavigate();
  const {
    carrinho,
    updateQuantity,
    updatePrice,
    removeItem,
    enviarCarrinho,
    clearCarrinho,
    isLoading: carrinhoLoading,
    error: carrinhoError,
    clearError
  } = useCarrinhoContext();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<string | null>(null);
  const [localError, setLocalError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [responseData, setResponseData] = useState<any>(null);
  const [editandoPreco, setEditandoPreco] = useState<string | null>(null);
  const [novoPreco, setNovoPreco] = useState('');

  //const vendedor = SessionUtil.getVendedor();
  const cliente = SessionUtil.getCliente();
  const cliCodigo = cliente?.cliCodigo;
  const cliNome = cliente?.cliNome;

  const items = Array.from(carrinho.items.values());
  const error = localError || carrinhoError;

  // Monitora erros e abre modal automaticamente
  React.useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setErrorDialogOpen(true);
    }
  }, [error]);

  const handleAtualizarQuantidade = (codigo: string, novaQuantidade: number) => {
    if (novaQuantidade <= 0) {
      removeItem(codigo);
    } else {
      updateQuantity(codigo, novaQuantidade);
    }
  };

  const handleRemoverItem = (codigo: string) => {
    setItemToRemove(codigo);
    setRemoveDialogOpen(true);
  };

  const handleConfirmarRemocao = () => {
    if (itemToRemove) {
      removeItem(itemToRemove);
      setItemToRemove(null);
    }
    setRemoveDialogOpen(false);
  };

  const handleCancelarRemocao = () => {
    setItemToRemove(null);
    setRemoveDialogOpen(false);
  };

  const handleEditarPreco = (codigo: string, precoAtual: number) => {
    setEditandoPreco(codigo);
    setNovoPreco(precoAtual.toString());
  };

  const handleSalvarPreco = (codigo: string) => {
    const item = carrinho.items.get(codigo);
    if (!item) return;

    const precoNum = parseFloat(novoPreco);
    const precoMin = item.precoMin || 0;

    if (isNaN(precoNum) || precoNum < precoMin) {
      setLocalError(`Preço não pode ser menor que ${FormatUtil.formatCurrency(precoMin)}`);
      return;
    }

    try {
      updatePrice(codigo, precoNum);
      setEditandoPreco(null);
      setLocalError('');
    } catch (err: any) {
      setLocalError(err.message);
    }
  };

  const handleCancelarEdicao = () => {
    setEditandoPreco(null);
    setNovoPreco('');
    setLocalError('');
  };

  const handleFinalizarClick = () => {
    if (items.length === 0) {
      setErrorMessage('Carrinho vazio. Adicione produtos antes de finalizar.');
      setErrorDialogOpen(true);
      return;
    }
    if (!cliCodigo) {
      setErrorMessage('Nenhum cliente selecionado. Por favor, selecione um cliente antes de finalizar o pedido.');
      setErrorDialogOpen(true);
      return;
    }

    setDialogOpen(true);
  };

  const handleConfirmarFinalizacao = async () => {
    try {
      setDialogOpen(false);
      clearError();
      setLocalError('');

      const response = await enviarCarrinho();

      if (response.retorno === 'ok') {
        setResponseData(response);
        setSuccessDialogOpen(true);
      } else {
        // Retorno negativo da API
        const errorMsg = (response as any).erro || response.mensagem || 'Erro desconhecido ao enviar o pedido';
        setErrorMessage(errorMsg);
        setErrorDialogOpen(true);
      }
    } catch (err: any) {
      // Erro de exception/rede
      const errorMsg = err.response?.data?.erro || err.message || 'Erro ao finalizar pedido. Tente novamente.';
      console.error('Erro ao enviar pedido:', err);
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
    }
  };

  const handleFecharSucesso = () => {
    setSuccessDialogOpen(false);
    clearCarrinho();
    SessionUtil.clearCliente();
    navigate('/classificacoes-pedido');
  };

  const handleFecharErro = () => {
    setErrorDialogOpen(false);
    setLocalError('');
    clearError();
  };

  if (items.length === 0 && !carrinhoLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <ShoppingCartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Carrinho Vazio
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Adicione produtos para criar seu pedido
        </Typography>
        {cliNome && (
          <Typography variant="caption" color="primary" sx={{ mb: 2, display: 'block' }}>
            Cliente selecionado: {cliNome}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={() => navigate('/classificacoes-pedido')}
        >
          Ver Produtos
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <PageHeader
        title="Carrinho de Pedido"
        subtitle={
          cliNome
            ? `${items.length} ${items.length === 1 ? 'produto' : 'produtos'} • Cliente: ${cliNome}`
            : `${items.length} ${items.length === 1 ? 'produto' : 'produtos'}`
        }
        showBackButton
        onBack={() => navigate(-1)}
      />

      {/* Lista de Itens */}
      <List sx={{ px: 1 }}>
        {items.map((item) => (
          <Card key={item.codigo} sx={{ mx: 1, mb: 1 }}>
            <ListItem sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, px: 2 }}>

              {/* Esquerda: Imagem + Info */}
              <Avatar
                src={item.imagem ? FormatUtil.formatImageUrl(item.imagem) : undefined}
                alt={item.nome}
                sx={{ width: 60, height: 60, flexShrink: 0, ml:2, mr:2 }}
                variant="rounded"
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                  {item.nome || item.codigo}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.unidade} • Peso: {FormatUtil.formatWeight(item.peso / item.quantidade)}
                </Typography>
              </Box>

              {/* Centro: Preço */}
              <Box sx={{ flexShrink: 0, textAlign: 'left', minWidth: '33%' }}>
                {editandoPreco === item.codigo ? (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={novoPreco}
                      onChange={(e) => setNovoPreco(e.target.value)}
                      label="Preço"
                      InputProps={{ startAdornment: 'R$ ' }}
                      sx={{ width: '70%' }}
                    />
                    <IconButton size="small" color="primary" onClick={() => handleSalvarPreco(item.codigo)}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelarEdicao}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box>
                      <Typography variant="body2">
                        Preço Unitário: <strong>{FormatUtil.formatCurrency(item.precoUnitario || 0)}</strong>
                      </Typography>
                      {item.precoMin && (
                        <Typography variant="caption" color="text.secondary">
                          Preço mínimo: {FormatUtil.formatCurrency(item.precoMin)}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditarPreco(item.codigo, item.precoUnitario || 0)}
                      disabled={carrinhoLoading}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {/* Direita: Controlador + Lixeira + Total */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ControladorQuantidade
                    quantidade={item.quantidade}
                    onIncrement={() => handleAtualizarQuantidade(item.codigo, item.quantidade + 1)}
                    onDecrement={() => handleAtualizarQuantidade(item.codigo, item.quantidade - 1)}
                    max={'estoque' in item ? item.estoque : 0}
                    disabled={carrinhoLoading}
                    usandoEstoqueLoja={'loja' in item && item.loja === 1}
                    size="medium"
                    editavel
                  />
                  <IconButton size="small" color="error" onClick={() => handleRemoverItem(item.codigo)} disabled={carrinhoLoading}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Typography variant="body2" fontWeight="bold" color="primary" sx={{ m:1.5 }}>
                  Total: {FormatUtil.formatCurrency(item.total || 0)}
                </Typography>
              </Box>

            </ListItem>
          </Card>
        ))}
      </List>

      {/* Totais */}
      <Card sx={{ mx: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Resumo do Pedido
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Total de Itens:</Typography>
            <Typography variant="body2" fontWeight="bold">
              {carrinho.quantidadeTotal}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Peso Total:</Typography>
            <Typography variant="body2" fontWeight="bold">
              {FormatUtil.formatWeight(carrinho.pesoTotal)}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">Total:</Typography>
            <Typography variant="h6" color="primary" fontWeight="bold">
              {FormatUtil.formatCurrency(carrinho.total)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Botão Finalizar */}
      <Box sx={{ px: 2 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={carrinhoLoading ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleFinalizarClick}
          disabled={carrinhoLoading || items.length === 0}
        >
          {carrinhoLoading ? 'Enviando...' : 'Finalizar Pedido'}
        </Button>
      </Box>

      {/* Diálogo de Confirmação */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar Pedido</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Deseja finalizar este pedido?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Cliente:</strong> {cliNome}
            </Typography>
            <Typography variant="body2">
              <strong>Itens:</strong> {carrinho.quantidadeTotal}
            </Typography>
            <Typography variant="body2">
              <strong>Peso Total:</strong> {FormatUtil.formatWeight(carrinho.pesoTotal)}
            </Typography>
            <Typography variant="body2">
              <strong>Valor Total:</strong> {FormatUtil.formatCurrency(carrinho.total)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={carrinhoLoading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmarFinalizacao} variant="contained" disabled={carrinhoLoading}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmação de Remoção */}
      <Dialog open={removeDialogOpen} onClose={handleCancelarRemocao} maxWidth="xs" fullWidth>
        <DialogTitle>Remover Item</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover este item do carrinho?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelarRemocao}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmarRemocao} variant="contained" color="error">
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Erro */}
      <Dialog open={errorDialogOpen} onClose={handleFecharErro} maxWidth="sm" fullWidth>
        <DialogTitle>Erro no Carrinho de Pedido</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Erro ao processar o pedido
          </Alert>
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2">
              {errorMessage}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          {errorMessage.includes('cliente') && (
            <Button
              onClick={() => {
                handleFecharErro();
                navigate('/clientes');
              }}
              variant="outlined"
            >
              Selecionar Cliente
            </Button>
          )}
          <Button onClick={handleFecharErro} variant="contained" color="error">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Sucesso */}
      <Dialog open={successDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Pedido Enviado com Sucesso!</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Pedido enviado com sucesso!
          </Alert>
          {responseData && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Previsão:</strong> {responseData.previsao}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Pedido:</strong> {responseData.pedido}
              </Typography>
              <Typography variant="body2">
                <strong>Produtos:</strong> {responseData.produtos}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharSucesso} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarrinhoPedido;
